import { MutationCache, QueryClient } from "@tanstack/react-query";
import type { Carrier, PriceType } from "../api/client";
import { ApiError, friendlyErrorMessage } from "../api/_error";
import { emitGlobalToast } from "../components/common/Toast";

const MINUTE = 60 * 1000;

const PRICES_QUERY_KEY = ["prices"] as const;

function normalizeSearch(search?: string): string {
  return search?.trim() ?? "";
}

// 재시도해도 결과가 안 바뀌는 실패는 재시도하지 않는다. 판정을 message 문자열이 아니라
// ApiError.status 로 하는 이유(2026-08-10):
//   ① 옛 코드는 `message.startsWith("HTTP 4")` 로 4xx 를 걸렀는데, api/_error.ts 가 상태코드를
//      message 에 싣지 않고 `.status` 로 옮긴 뒤로 그 조건이 사실상 항상 false 였다 —
//      즉 4xx 도 전부 한 번씩 재시도되고 있었다.
//   ② 서버가 요청 상한(60초/165초)을 넘겨 끊은 503 을 재시도하면, 첫 요청의 쿼리는 아직 살아 있는
//      상태에서 같은 무거운 조회가 main 풀 4칸에 하나 더 들어간다 — "가장 가벼운 조회가 먼저
//      죽는" 정체를 완화하려고 넣은 방어가 오히려 압력을 두 배로 만든다.
//      서버는 이 경우 본문에 `code:"request_timeout"` 과 `retrySafe:false` 를 실어 준다.
function shouldRetry(failureCount: number, error: Error): boolean {
  if (error instanceof ApiError) {
    if (error.status >= 400 && error.status < 500) return false;
    const body = error.payload as { code?: unknown; retrySafe?: unknown } | null;
    if (body && typeof body === "object") {
      if (body.code === "request_timeout") return false;
      if (body.retrySafe === false) return false;
    }
  }
  // 옛 형태(상태코드가 message 에 박힌 문자열)도 계속 걸러 준다 — 아직 남은 호출부가 있을 수 있다.
  if (error.message.startsWith("HTTP 4")) {
    return false;
  }

  return failureCount < 1;
}

export const queryClient = new QueryClient({
  // 전역 안전망: 어떤 mutation 이든 실패하면(개별 onError 유무와 무관) 화면에 토스트로 소리낸다.
  // "서버는 500 인데 화면은 조용히 그대로"인 조용한 실패를 원천 차단한다 — 2026-07-01 개통 CRM
  // 단계변경 500 장애(프론트가 에러를 삼켜 사용자엔 "그냥 안 됨"으로 보임) 교훈. error.message 는
  // api/_error.ts 에서 이미 고객 안전 문구로 정규화돼 그대로 노출해도 된다.
  // 자체적으로 에러를 표시하는 mutation 은 meta:{ silentError:true } 로 이 토스트만 끈다.
  mutationCache: new MutationCache({
    onError: (error, _vars, _ctx, mutation) => {
      if (mutation.meta?.silentError) return;
      // ApiError 가 아닌 예외(TypeError·파싱 실패 등)의 원문은 사람 말로 바꿔 띄운다.
      emitGlobalToast("error", friendlyErrorMessage(error));
    },
  }),
  defaultOptions: {
    queries: {
      staleTime: 30 * 1000,
      gcTime: 10 * MINUTE,
      refetchOnWindowFocus: false,
      retry: shouldRetry,
      retryOnMount: false,
    },
  },
});

// ── CRM 기준값(마스터)은 오래 들고 있는다 (2026-08-11, task 472) ─────────────────
//
// 대리점·요금제·고객유형·부가서비스·결제방법 같은 **기준값**은 매장이 어쩌다 한 번 손보는
// 목록이다. 그런데 이 목록을 콤보박스·모달·현황 페이지 등 9곳이 각자 useQuery 로 부르고 있어,
// 전역 staleTime(30초)이 지나면 화면을 옮길 때마다 다시 물어봤다. 고객관리 화면이 한 번에
// 요청 8개를 던진 팬아웃의 한 축이 이것이다.
//
// 훅 9곳에 한 줄씩 넣는 대신 **키 접두어 하나에 기본값을 준다** — 호출부는 한 글자도 안 바뀌고,
// 나중에 생길 마스터 조회도 자동으로 이 규칙을 따른다(호출부에서 개별 지정하면 그쪽이 이긴다).
//
// ⚠️ staleTime 은 **자동 재요청만** 억제한다. 마스터를 편집하는 mutation 들은 그대로
// invalidateQueries(crmKeys.master(kind)) 를 부르므로, 내가 고친 값은 여전히 즉시 반영된다.
// 남이 고친 값이 최대 이 시간만큼 늦게 보이는 것이 유일한 대가다 — 기준값의 성격상 허용된다.
const CRM_MASTER_STALE_MS = 30 * MINUTE;
queryClient.setQueryDefaults(["crm", "master"], { staleTime: CRM_MASTER_STALE_MS });
// 단말기 모델·요금제 카탈로그도 같은 성격(제조사가 내는 목록, 매장이 못 바꾼다).
queryClient.setQueryDefaults(["crm", "phoneModels"], { staleTime: CRM_MASTER_STALE_MS });
queryClient.setQueryDefaults(["crm", "plans"], { staleTime: CRM_MASTER_STALE_MS });

// ── 정책 기준값(마스터)도 같은 수법으로 (2026-08-28 팬아웃 줄이기) ───────────────
//
// 정책 쿼리는 대부분 돈이 걸린 값(시세·리베이트·재고)이라 전역 기본 30초를 그대로 쓴다.
// 예외가 둘 있다 — **표준 구간(plan-buckets)** 과 **모델 카탈로그(models)**. 둘 다 사람이
// 어쩌다 한 번 손보는 목록이고, 정책 업로드·확정으로는 바뀌지 않는다(policyApi.ts 의
// invalidatePolicyQueries 주석이 모델 카탈로그를 무효화 대상에서 뺀 근거와 같은 성격).
//
// 이 코드베이스는 이미 이 둘을 "5분짜리"로 취급해 왔다 — 호출부 20여 곳이 staleTime 5분을
// 손으로 적어 뒀다. 그런데 안 적은 자리가 남아 30초로 돌고 있었다: 표준 구간은
// PolicyPlanGroupManageModal·PolicyRebateMatrixPage·PolicyInlineMappingControl·
// PolicyPendingMappingsModal·PolicyAliasesPage, 모델 카탈로그는 PolicyUnmappedModelAlert.
// 그 자리들은 모달을 여닫거나 화면을 오갈 때마다 30초만 지나면 다시 물었다.
// 그래서 **새 숫자를 들이지 않고** 이미 합의된 5분을 접두어 기본값으로 승격한다.
//
// ⚠️ staleTime 은 자동 재요청만 억제한다. 표준 구간 편집은 invalidatePolicyQueries(["policy"]
// 접두어)로, 모델 이름 편집은 PolicyCatalogPage 가 ["policy","models"] 를 직접 지워 무효화하므로
// **내가 고친 값은 여전히 즉시 반영된다.** 남이 고친 값이 최대 5분 늦게 보이는 것이 유일한 대가다.
const POLICY_MASTER_STALE_MS = 5 * MINUTE;
queryClient.setQueryDefaults(["policy", "plan-buckets"], { staleTime: POLICY_MASTER_STALE_MS });
queryClient.setQueryDefaults(["policy", "models"], { staleTime: POLICY_MASTER_STALE_MS });

/**
 * 무거운 정책 화면(리베이트 합산 board, 시세표 meta·cells)용 공유 쿼리 옵션.
 *
 * 정책: **정확성 우선**(가격은 돈이 걸린 값). staleTime=0 으로 두어 화면 진입·탭 복귀마다
 * 항상 "지금 최신인지" 재검증한다 — 다른 직원·다른 탭이 방금 바꾼 정책도 거의 즉시 반영된다.
 *
 * 그런데도 느려지지 않는 이유(세 장치가 비용을 상쇄):
 *  1) persist(IndexedDB): 진입 즉시 직전 표를 0ms 로 띄운다 — 빈 화면·대기 없음.
 *  2) ETag/304: 그 직후 도는 재검증은 정책이 안 바뀌었으면 본문 0바이트(304)라 거의 공짜.
 *     바뀌었을 때만 새 본문을 받아 수백 ms 안에 갱신.
 *  3) 서버 L1·DB 캐시: 재검증이 본문을 받아야 할 때도 재계산 없이 캐시에서 즉시.
 * 즉 staleTime 을 버려 정확성을 얻되, persist+ETag 덕에 체감 속도는 유지된다.
 *
 * refetchOnWindowFocus 도 전역 default(false)를 깨고 true — 탭 복귀 시에도 재검증(역시 304면 공짜).
 * 정책 변경 mutation 은 invalidatePolicyQueries 가 즉시 무효화하므로, 본인이 고친 변경은 0초 반영.
 */
export const POLICY_HEAVY_QUERY_OPTS = {
  staleTime: 0,
  refetchOnWindowFocus: true,
} as const;

export const queryKeys = {
  tenants: () => ["tenants"] as const,
  // ─── 가격비교 도메인 (정책관리 복원) ─────────────────────
  pricesRoot: PRICES_QUERY_KEY,
  prices: (params: {
    carrier: Carrier;
    planGroup: string;
    priceType: PriceType;
    asOf: string;
    search?: string;
  }) =>
    [
      ...PRICES_QUERY_KEY,
      params.carrier,
      params.planGroup,
      params.priceType,
      params.asOf,
      normalizeSearch(params.search),
    ] as const,
  availableDates: () => ["availableDates"] as const,
  connections: () => ["connections"] as const,
  uploadCalendar: (month: string) => ["uploadCalendar", month] as const,
  retailers: () => ["retailers"] as const,
};

/** KST 기준 오늘 YYYY-MM-DD. 서버 UTC 폴백과 최대 1일 어긋남을 차단한다. */
export function todayKst(): string {
  return new Intl.DateTimeFormat("sv-SE", { timeZone: "Asia/Seoul" }).format(new Date());
}
